import React from 'react';
import { Plus, Loader2, Save } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

export const PRESET_ICONS = ['⚙️', '📊', '🎯', '📈', '💼', '🏆', '⭐', '🔥', '💡', '🚀', '📅', '🎨'];

export const PRESET_COLORS = [
  { id: 'slate', name: 'Gray', class: 'border-slate-300 hover:border-slate-500 hover:bg-slate-50' },
  { id: 'blue', name: 'Blue', class: 'border-blue-300 hover:border-blue-500 hover:bg-blue-50' },
  { id: 'teal', name: 'Teal', class: 'border-teal-300 hover:border-teal-500 hover:bg-teal-50' },
  { id: 'purple', name: 'Purple', class: 'border-purple-300 hover:border-purple-500 hover:bg-purple-50' },
  { id: 'amber', name: 'Amber', class: 'border-amber-300 hover:border-amber-500 hover:bg-amber-50' },
  { id: 'rose', name: 'Rose', class: 'border-rose-300 hover:border-rose-500 hover:bg-rose-50' },
  { id: 'emerald', name: 'Emerald', class: 'border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50' },
];

const EMPTY_PRESET = { name: '', description: '', icon: '⚙️', color: 'slate' };

export default function SavePresetDialog({
  open,
  onOpenChange,
  newPreset,
  setNewPreset,
  onSave,
  saving,
}) {
  const handleClose = () => {
    onOpenChange(false);
    setNewPreset(EMPTY_PRESET);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-teal-600" /> Save Custom Preset
          </DialogTitle>
          <DialogDescription>Save current settings as a reusable preset.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="preset-name" className="text-sm font-medium">
              Preset Name <span className="text-rose-500">*</span>
            </Label>
            <Input
              id="preset-name"
              placeholder="e.g., Q1 Planning"
              value={newPreset.name}
              onChange={(e) => setNewPreset((prev) => ({ ...prev, name: e.target.value }))}
              className="mt-1"
              data-testid="preset-name-input"
            />
          </div>
          <div>
            <Label htmlFor="preset-description" className="text-sm font-medium">
              Description (optional)
            </Label>
            <Input
              id="preset-description"
              placeholder="Brief description"
              value={newPreset.description}
              onChange={(e) => setNewPreset((prev) => ({ ...prev, description: e.target.value }))}
              className="mt-1"
              data-testid="preset-description-input"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Icon</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {PRESET_ICONS.map((icon) => (
                <button
                  key={icon}
                  onClick={() => setNewPreset((prev) => ({ ...prev, icon }))}
                  className={`w-10 h-10 text-xl rounded-lg border-2 transition-all ${
                    newPreset.icon === icon
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">Color</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color.id}
                  onClick={() => setNewPreset((prev) => ({ ...prev, color: color.id }))}
                  className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-all ${
                    newPreset.color === color.id
                      ? 'border-teal-500 bg-teal-50 font-medium'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {color.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button
            onClick={onSave}
            disabled={saving || !newPreset.name.trim()}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="confirm-save-preset-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Preset
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
